import { uploadDestination } from '@hive/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription } from '@/components/ui/dialog';
import { Select } from '@/components/ui/field';
import { queryKeys } from '@/data/keys';
import { cn } from '@/lib/utils';
import { formatBytes } from './model';
import { UploadOpener } from './upload-context';
import { liveDeps } from './liveDeps';
import { destinationProblem, uploadAttachment, type Destination, type UploadContext } from './upload';
import { useDriveStatus } from './useDrive';

interface Row {
  file: File;
  destination: Destination;
  progress: number | null;
  error: string | null;
  done: boolean;
}

/**
 * Upload one or more files. Each goes where the design doc says by default
 * (Hive storage for images and files under 25 MB, Drive for video and big
 * files) and either founder can switch it. Reused by Comms in Phase 5.
 */
export function UploadDialog({
  files,
  context,
  onClose,
}: {
  files: readonly File[];
  context: UploadContext;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const drive = useDriveStatus();
  const [rows, setRows] = useState<Row[]>(() =>
    files.map((file) => ({ file, destination: uploadDestination(file), progress: null, error: null, done: false })),
  );
  const [running, setRunning] = useState(false);
  const patch = (i: number, p: Partial<Row>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...p } : r)));
  const problems = rows.map((r) => (r.done ? null : destinationProblem(r.file, r.destination, drive)));
  const ready = rows.some((r, i) => !r.done && !problems[i]);

  async function run() {
    setRunning(true);
    for (const [i, row] of rows.entries()) {
      if (row.done || problems[i]) continue;
      patch(i, { progress: 0, error: null });
      try {
        await uploadAttachment(
          row.file,
          row.destination,
          context,
          drive.rootFolderId,
          (f) => patch(i, { progress: f }),
          liveDeps,
        );
        patch(i, { done: true, progress: 1 });
      } catch (e) {
        patch(i, { error: e instanceof Error ? e.message : 'Upload failed.', progress: null });
      }
    }
    setRunning(false);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.files }),
      queryClient.invalidateQueries({ queryKey: queryKeys.vaultLinks }),
      queryClient.invalidateQueries({ queryKey: queryKeys.links }),
    ]);
  }

  const allDone = rows.every((r) => r.done);
  return (
    <Dialog open onOpenChange={(open) => !open && !running && onClose()}>
      <DialogContent title={files.length === 1 ? 'Upload a file' : `Upload ${files.length} files`}>
        <DialogDescription className="text-sm text-muted">
          Images and files under 25 MB go to Hive storage (images are shrunk to WebP). Video and bigger files go
          straight to Google Drive, in Hive Uploads / {context.gameName} / {context.module}.
        </DialogDescription>
        <ul className="flex flex-col gap-2">
          {rows.map((row, i) => (
            <li key={`${row.file.name}-${i}`} className="flex flex-col gap-1.5 bg-bg-3/60 p-2.5">
              <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm" title={row.file.name}>
                  {row.file.name}
                </span>
                <span className="font-mono text-[11px] text-muted">{formatBytes(row.file.size)}</span>
                <Select
                  aria-label={`Where ${row.file.name} goes`}
                  className="w-auto"
                  value={row.destination}
                  disabled={running || row.done}
                  onChange={(e) => patch(i, { destination: e.target.value as Destination })}
                >
                  <option value="hive">Hive storage</option>
                  <option value="drive">Google Drive</option>
                </Select>
              </div>
              {row.progress !== null ? (
                <div
                  role="progressbar"
                  aria-label={`Uploading ${row.file.name}`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(row.progress * 100)}
                  className="h-1.5 bg-bg"
                >
                  <div
                    className={cn('h-full transition-[width]', row.done ? 'bg-green' : 'bg-cyan')}
                    style={{ width: `${Math.round(row.progress * 100)}%` }}
                  />
                </div>
              ) : null}
              {row.done ? <p className="font-mono text-[11px] text-green">Uploaded</p> : null}
              {problems[i] ? <p className="font-mono text-[11px] text-yellow">{problems[i]}</p> : null}
              {row.error ? (
                <p role="alert" className="font-mono text-[11px] text-red">
                  {row.error}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={running}>
            {allDone ? 'Close' : 'Cancel'}
          </Button>
          {!allDone ? (
            <Button onClick={() => void run()} disabled={running || !ready}>
              {running ? 'Uploading…' : 'Upload'}
            </Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Anything that accepts attachments: drop files on it or paste them in, and
 * the upload dialog opens for them, attaching the results to `context.attachTo`.
 */
export function AttachmentDropZone({
  context,
  className,
  children,
}: {
  context: UploadContext;
  className?: string;
  children: ReactNode;
}) {
  const [files, setFiles] = useState<File[] | null>(null);
  const [over, setOver] = useState(false);
  const open = useCallback((list: File[]) => {
    if (list.length) setFiles(list);
  }, []);
  return (
    <UploadOpener value={open}>
      <div
        className={cn(className, over && 'outline-2 -outline-offset-2 outline-cyan outline-dashed')}
        onDragOver={(e) => {
          if (!e.dataTransfer.types.includes('Files')) return;
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
        }}
        onDrop={(e) => {
          if (!e.dataTransfer.files.length) return;
          e.preventDefault();
          setOver(false);
          open([...e.dataTransfer.files]);
        }}
        onPaste={(e) => {
          const pasted = [...e.clipboardData.files];
          if (pasted.length) {
            e.preventDefault();
            open(pasted);
          }
        }}
      >
        {children}
      </div>
      {files ? <UploadDialog files={files} context={context} onClose={() => setFiles(null)} /> : null}
    </UploadOpener>
  );
}
